/**
 * Ephemeral "listing context" that follows a visitor from /woningen into a
 * property detail page and back again.
 *
 * Why sessionStorage instead of URL params or React context:
 *   - URL params would require refactoring every filter to a controlled URL
 *     state, and they leak into shared links (someone shares /woningen?filters
 *     and their friend sees the filters — undesirable for a marketing landing).
 *   - React context is lost the moment the user hits browser Back after
 *     hard-navigating to a detail page, then reloads.
 *   - sessionStorage survives back/forward inside a single tab, is scoped
 *     per-tab (won't leak between multiple product searches), and disappears
 *     when the tab closes.
 *
 * Payload shape:
 *   {
 *     filters, sortBy, searchQuery, filtersOpen  // full listing state
 *     scrollY                                    // exact pixel offset
 *     filteredIds                                // ordered id list for prev/next
 *     ts                                         // unix ms — used to expire
 *   }
 *
 * Expiration:
 *   Entries older than STALE_MS are treated as absent. This prevents a user
 *   returning to /woningen an hour later from getting weirdly-scrolled to
 *   an outdated list (data may have changed upstream).
 */
const KEY = 'casaothers.propertiesListState';
const STALE_MS = 30 * 60 * 1000; // 30 minutes

const safeParse = (raw) => {
  try { return JSON.parse(raw); } catch { return null; }
};

export const saveListingState = (state) => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...state, ts: Date.now() }));
  } catch { /* quota exceeded or private-mode block — noop */ }
};

export const readListingState = () => {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(KEY);
  if (!raw) return null;
  const parsed = safeParse(raw);
  if (!parsed || typeof parsed.ts !== 'number') return null;
  if (Date.now() - parsed.ts > STALE_MS) return null;
  return parsed;
};

export const clearListingState = () => {
  if (typeof window === 'undefined') return;
  try { sessionStorage.removeItem(KEY); } catch { /* noop */ }
};

/**
 * Patch a partial into the current stored state. Used when the visitor only
 * updates their scroll position (frequent) without touching filters.
 */
export const patchListingState = (patch) => {
  const current = readListingState() || {};
  saveListingState({ ...current, ...patch });
};
